import {
  isBoundUrlShape,
  isUrlSourceClass,
  normalizeBindableUrl,
  verifyBoundUrl,
  type BoundUrl,
  type BoundUrlPublicKeys,
  type UrlSourceClass,
} from "../security/boundUrl.ts";
import type {
  LinkCheckRequest,
  LinkCheckResponse,
  LinkOpenPolicy,
  LinkReason,
} from "../security/linkCheck.ts";
import { boundUrlKey, boundUrlUser, isCodeBoundUrl } from "./boundUrlSet.ts";

/**
 * The client half of the platform link check (docs/security/injection-quarantine.md,
 * "Link check"; contract in STATUS.inj1c-linkcheck.md). Smart Manager checks
 * a link before anything loads it, so the user never has to judge an
 * address. Every in-app viewer load, every page-to-page navigation the
 * desktop holds, and every open outside the app asks here first.
 *
 * - The server's verdict decides how the page opens: `blocked` never loads,
 *   `hardened` loads in the read-only throwaway profile, `normal` loads.
 * - The page that loads is the checked final URL (a short link expanded),
 *   and only once its binding verifies for this user.
 * - A check that fails never counts as clean: a refusal (400, 401, 403,
 *   413) blocks; no answer (a network failure, a timeout, 429, 5xx) opens
 *   hardened.
 * - A code constant from this bundle (bindPlatformUrl, bindFirstPartyPath)
 *   is ours, so it opens normally with no request.
 *
 * Only a tap's answer carries a binding (the final URL, bound as the tapped
 * class when it is the tapped address, else as outside text). The viewer
 * keeps it as its tab's root binding for the tab's life: every later
 * navigation is checked `from` it, and a navigation's answer binds nothing.
 * Bindings stay with the viewer that asked. They never join the session's
 * bound set and never ask for a tap grant (link check rule 7).
 */

/** What a link check is about. */
export type LinkCheckTarget =
  /** A link the server bound (a tap, a grant, a restored window). */
  | Readonly<{ kind: "bound"; binding: BoundUrl }>
  /** A top-level navigation away from a checked page. */
  | Readonly<{ kind: "navigation"; from: BoundUrl; to: string }>
  /**
   * An address no server binding covers: typed into the bar, or reached from
   * a page without a binding (`navigationFrom`: the address that tab opened
   * with; it picks the lookup quota only, never the answer).
   */
  | Readonly<{ kind: "unbound"; url: string; typed: boolean; navigationFrom?: string }>;

/** Sends one link check and resolves to its JSON body; rejects with `{ status }` on an HTTP error. */
export type LinkCheckRequester = (request: LinkCheckRequest) => Promise<unknown>;

/**
 * Why a link has no server verdict:
 * - `refused`: the server would not check it (not this user's link, a bad
 *   address, signed out). It never loads.
 * - `unavailable`: the check did not answer. It opens hardened.
 */
export type LinkCheckFailure = "refused" | "unavailable";

export type LinkGateResult = Readonly<{
  policy: LinkOpenPolicy;
  /** The address to load (the checked final URL), or null when blocked. */
  loadUrl: string | null;
  /**
   * A tap only: the verified binding of `loadUrl`, the root every later
   * navigation in its tab is checked from. Null for a navigation or an
   * unbound address (the server binds nothing for them), a code constant, a
   * refusal, or a final URL that did not verify.
   */
  pageBinding: BoundUrl | null;
  reasons: readonly LinkReason[];
  /** The final host as a person reads it. */
  displayHost: string;
  /** Attribution a reputation list requires beside its warning; show it when set. */
  advisory: string | null;
  /** True when the check went to another address than the one asked about (a short link expanded). */
  redirected: boolean;
  /** The final page's class (a navigation's or an expansion's target is outside text). */
  sourceClass: UrlSourceClass | null;
  failure: LinkCheckFailure | null;
}>;

/** A check this old is asked again; the server's own verdict cache is shorter. */
const CACHE_TTL_MS = 10 * 60_000;
const MAX_CACHED = 400;
/** Statuses that mean the server refused to check this link. */
const REFUSED_STATUSES = new Set([400, 401, 403, 413]);

type Installed = { request: LinkCheckRequester; keys: () => BoundUrlPublicKeys };
let installed: Installed | null = null;

type CacheEntry = { result: LinkGateResult; at: number };
const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<LinkGateResult>>();
let cacheUser: string | null = null;
// Bumped when the user changes or the checker is replaced, so a check that started before drops its result.
let generation = 0;

/**
 * Installs the app's link check transport (POST /api/security/link-check)
 * and the server's public keys. Install once per page; returns the uninstall.
 */
export function installLinkChecker(request: LinkCheckRequester, keys: () => BoundUrlPublicKeys): () => void {
  const mine: Installed = { request, keys };
  installed = mine;
  resetCache();
  return () => {
    if (installed !== mine) return;
    installed = null;
    resetCache();
  };
}

/** True when this page can check links (the signed-in app installed a checker). */
export function linkCheckerInstalled(): boolean {
  return installed !== null;
}

function resetCache(): void {
  cache.clear();
  inFlight.clear();
  generation += 1;
}

function scopeToUser(): string | null {
  const user = boundUrlUser();
  if (user !== cacheUser) {
    cacheUser = user;
    resetCache();
  }
  return user;
}

/** The address a target asks about. */
export function linkCheckTargetUrl(target: LinkCheckTarget): string {
  switch (target.kind) {
    case "bound": return target.binding.url;
    case "navigation": return target.to;
    case "unbound": return target.url;
  }
}

/**
 * The cache key: the address, the class it is checked as, and whether the
 * answer carries a binding (a tap) or not. A navigation's target and an
 * unbound address are both checked as outside text and bind nothing, so
 * either reuses the other's answer.
 */
function checkClass(target: LinkCheckTarget): UrlSourceClass {
  switch (target.kind) {
    case "bound": return target.binding.sourceClass;
    case "navigation": return "outside_text";
    case "unbound": return target.typed ? "user_typed" : "outside_text";
  }
}

function cacheKey(target: LinkCheckTarget): string {
  const answer = target.kind === "bound" ? "tap" : "open";
  return `${answer}\n${checkClass(target)}\n${comparableUrl(linkCheckTargetUrl(target))}`;
}

/** The address without its fragment: a jump within a page is the same page. */
export function comparableUrl(url: string): string {
  const hash = url.indexOf("#");
  return hash === -1 ? url : url.slice(0, hash);
}

function wireRequest(target: LinkCheckTarget): LinkCheckRequest {
  switch (target.kind) {
    case "bound": return { boundUrl: target.binding };
    case "navigation": return { navigation: { from: target.from, to: target.to } };
    case "unbound": return {
      unboundUrl: {
        url: target.url,
        typed: target.typed,
        ...(target.navigationFrom !== undefined && !target.typed ? { navigationFrom: target.navigationFrom } : {}),
      },
    };
  }
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** A code constant from this bundle: ours, so it opens normally. */
function codeResult(url: string): LinkGateResult {
  return {
    policy: "normal",
    loadUrl: url,
    pageBinding: null,
    reasons: [],
    displayHost: hostOf(url),
    advisory: null,
    redirected: false,
    sourceClass: "template_clean",
    failure: null,
  };
}

const UNAVAILABLE_REASON: LinkReason = {
  code: "check_unavailable",
  severity: "caution",
  label: "Smart Manager could not finish checking this link.",
};

/** No verdict: refused never loads; unavailable opens the asked-for address hardened. */
function failedResult(target: LinkCheckTarget, failure: LinkCheckFailure): LinkGateResult {
  const url = linkCheckTargetUrl(target);
  const refused = failure === "refused";
  return {
    policy: refused ? "blocked" : "hardened",
    loadUrl: refused ? null : url,
    pageBinding: refused || target.kind !== "bound" ? null : target.binding,
    reasons: refused ? [] : [UNAVAILABLE_REASON],
    displayHost: hostOf(url),
    advisory: null,
    redirected: false,
    sourceClass: target.kind === "bound" ? target.binding.sourceClass : null,
    failure,
  };
}

function failureOf(error: unknown): LinkCheckFailure {
  const status = error && typeof error === "object" ? (error as { status?: unknown }).status : undefined;
  return typeof status === "number" && REFUSED_STATUSES.has(status) ? "refused" : "unavailable";
}

const POLICIES = new Set<LinkOpenPolicy>(["blocked", "hardened", "normal"]);

function isResponse(value: unknown): value is LinkCheckResponse {
  if (!value || typeof value !== "object") return false;
  const body = value as Partial<Record<keyof LinkCheckResponse, unknown>>;
  return typeof body.policy === "string"
    && POLICIES.has(body.policy as LinkOpenPolicy)
    && typeof body.finalUrl === "string"
    && Array.isArray(body.reasons)
    && typeof body.complete === "boolean";
}

/** The strictest of two policies. */
function stricter(a: LinkOpenPolicy, b: LinkOpenPolicy): LinkOpenPolicy {
  if (a === "blocked" || b === "blocked") return "blocked";
  if (a === "hardened" || b === "hardened") return "hardened";
  return "normal";
}

async function fromResponse(
  target: LinkCheckTarget,
  body: LinkCheckResponse,
  userId: string,
  keys: BoundUrlPublicKeys,
): Promise<LinkGateResult> {
  const asked = linkCheckTargetUrl(target);
  const reasons = Array.isArray(body.reasons) ? body.reasons : [];
  const displayHost = typeof body.displayHost === "string" && body.displayHost ? body.displayHost : hostOf(body.finalUrl);
  const advisory = typeof body.advisory === "string" && body.advisory ? body.advisory : null;
  const blocked: LinkGateResult = {
    policy: "blocked",
    loadUrl: null,
    pageBinding: null,
    reasons,
    displayHost,
    advisory,
    redirected: comparableUrl(body.finalUrl) !== comparableUrl(asked),
    sourceClass: null,
    failure: null,
  };
  if (body.policy === "blocked") return blocked;
  // Any part of the check that did not finish (a hidden destination, a
  // signal that did not answer) opens hardened, whatever the verdict.
  const policy = body.complete ? body.policy : stricter(body.policy, "hardened");

  if (target.kind !== "bound") {
    // A navigation or an unbound address binds nothing: load the checked
    // final page under the policy. The tab keeps the binding it opened with.
    // The server's own rule for a page that may load (https, no credentials).
    const loadUrl = normalizeBindableUrl(body.finalUrl);
    if (!loadUrl) return blocked;
    return {
      policy,
      loadUrl,
      pageBinding: null,
      reasons,
      displayHost,
      advisory,
      redirected: comparableUrl(loadUrl) !== comparableUrl(asked),
      sourceClass: isUrlSourceClass(body.sourceClass) ? body.sourceClass : "outside_text",
      failure: null,
    };
  }

  // A tap's final page loads only as the exact bytes the server signed for
  // this user; the server forces `blocked` when it bound nothing, and the
  // client holds the same line.
  const binding = body.finalBoundUrl;
  if (!isBoundUrlShape(binding)) return blocked;
  const verified = await verifyBoundUrl(binding, userId, keys);
  if (!verified) return failedResult(target, "unavailable");
  return {
    policy,
    loadUrl: binding.url,
    pageBinding: binding,
    reasons,
    displayHost,
    advisory,
    redirected: comparableUrl(binding.url) !== comparableUrl(boundUrlKey(asked) ?? asked),
    sourceClass: binding.sourceClass,
    failure: null,
  };
}

/**
 * The answer already known for `target` this session, or null. Lets a
 * viewer show a page it checked before at once, without a "Checking" flash.
 */
export function cachedLinkCheck(target: LinkCheckTarget, now: number = Date.now()): LinkGateResult | null {
  const url = linkCheckTargetUrl(target);
  if (target.kind !== "navigation" && isCodeBoundUrl(url)) return codeResult(boundUrlKey(url) ?? url);
  scopeToUser();
  const entry = cache.get(cacheKey(target));
  if (!entry) return null;
  if (now - entry.at > CACHE_TTL_MS) {
    cache.delete(cacheKey(target));
    return null;
  }
  return entry.result;
}

function remember(key: string, result: LinkGateResult, now: number): void {
  cache.delete(key);
  cache.set(key, { result, at: now });
  while (cache.size > MAX_CACHED) {
    const oldest = cache.keys().next();
    if (oldest.done) break;
    cache.delete(oldest.value);
  }
}

/**
 * Checks `target` before anything loads it and resolves to how it may open.
 * Never rejects. Two checks of one address share one request; a complete
 * server answer is kept for the session (per user). Without an installed
 * checker or a signed-in user nothing can be checked, so the link opens
 * hardened (never normal).
 */
export function checkLinkBeforeOpen(target: LinkCheckTarget): Promise<LinkGateResult> {
  const cached = cachedLinkCheck(target);
  if (cached) return Promise.resolve(cached);
  const userId = scopeToUser();
  const checker = installed;
  if (!checker || !userId) return Promise.resolve(failedResult(target, "unavailable"));
  const key = cacheKey(target);
  const running = inFlight.get(key);
  if (running) return running;
  const started = generation;
  const task = (async (): Promise<LinkGateResult> => {
    let body: unknown;
    try {
      body = await checker.request(wireRequest(target));
    } catch (error) {
      return failedResult(target, failureOf(error));
    }
    if (!isResponse(body)) return failedResult(target, "unavailable");
    const result = await fromResponse(target, body, userId, checker.keys());
    // Only a complete server verdict is kept: a hardened open for want of an
    // answer asks again next time.
    if (started === generation && result.failure === null && body.complete) remember(key, result, Date.now());
    return result;
  })().finally(() => {
    if (inFlight.get(key) === task) inFlight.delete(key);
  });
  inFlight.set(key, task);
  return task;
}

/** Test seam: forgets the checker and every answer. */
export function resetLinkGateForTest(): void {
  installed = null;
  cacheUser = null;
  resetCache();
}
